import React, { useState, useEffect, useContext, useMemo } from 'react';
import { AuthContext } from '../../contexts/AuthContext';
import { RendererContext } from '../../contexts/RendererContex';
import { useSession } from '../../contexts/SessionContext';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import apiService from '../../services/api.service';
import {
  Card, Button, IconButton, Badge, Icon as OpusIcon, Avatar, LoadingState, BADGE_TONES,
} from '../common/opus';

/* OPUS-913 — Dashboard overview, ported from the Claude Design handoff
 * (app/screen-dashboard.jsx). Welcome header + clickable stat cards + a tabbed
 * chart card (SVG activity bars / renderer-status donut) + recent activity.
 * Visuals match the design; data is the live org feed (no fabricated trends). */

const ACTION_TONE = {
  CREATE: 'green', UPDATE: 'violet', DELETE: 'coral', LOGIN: 'blue',
  LOGOUT: 'neutral', VIEW: 'neutral', UPLOAD: 'green', RENDERER_ACTION: 'amber',
};
const ACTION_ICON = {
  CREATE: 'plus', UPDATE: 'edit', DELETE: 'trash', LOGIN: 'logout',
  LOGOUT: 'logout', VIEW: 'eye', UPLOAD: 'layers', RENDERER_ACTION: 'sliders',
};
const TYPE_ICON = { renderer: 'rendererList', scene: 'scene', user: 'users', asset: 'gallery', organization: 'org' };

/* ---------- Activity bar chart (SVG, scales to width) ---------- */
function ActivityBars({ days, values }) {
  const W = 720, H = 260, padL = 46, padR = 14, padT = 16, padB = 34;
  const plotW = W - padL - padR, plotH = H - padT - padB;
  const slot = plotW / Math.max(days.length, 1), bw = Math.min(48, slot * 0.52);
  const rawMax = Math.max(...values, 4);
  const max = Math.ceil(rawMax / 4) * 4; // 5 even ticks (0..max)
  const yFor = (v) => padT + plotH - (v / max) * plotH;
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((f) => Math.round(max * f));
  const peak = Math.max(...values);
  return (
    <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ display: 'block', overflow: 'visible' }} role="img" aria-label="Activities over the last 7 days">
      <defs>
        <linearGradient id="barFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="var(--coral)" stopOpacity="0.95" />
          <stop offset="100%" stopColor="var(--coral)" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      {ticks.map((t) => (
        <g key={t}>
          <line x1={padL} y1={yFor(t)} x2={W - padR} y2={yFor(t)} stroke="var(--border)" strokeWidth="1" strokeDasharray={t === 0 ? '0' : '3 5'} />
          <text x={padL - 10} y={yFor(t) + 4} textAnchor="end" fontSize="11" fill="var(--text-dim)" fontFamily="var(--font-sans)">{t}</text>
        </g>
      ))}
      {days.map((d, i) => {
        const v = values[i] || 0;
        const x = padL + i * slot + (slot - bw) / 2;
        const y = yFor(v), h = padT + plotH - y;
        const isPeak = peak > 0 && v === peak;
        return (
          <g key={d + i}>
            <rect x={x} y={y} width={bw} height={Math.max(h, 2)} rx="6" fill={isPeak ? 'var(--coral)' : 'url(#barFill)'} opacity={isPeak ? 1 : 0.9} />
            {isPeak && <text x={x + bw / 2} y={y - 8} textAnchor="middle" fontSize="12" fontWeight="700" fill="var(--text)" fontFamily="var(--font-sans)">{v}</text>}
            <text x={x + bw / 2} y={H - padB + 20} textAnchor="middle" fontSize="11" fill="var(--text-dim)" fontFamily="var(--font-sans)">{d}</text>
          </g>
        );
      })}
    </svg>
  );
}

/* ---------- Renderer status donut ---------- */
function StatusDonut({ segments, total }) {
  const r = 64, cx = 90, cy = 90, C = 2 * Math.PI * r;
  let offset = 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 26, padding: '10px 6px' }}>
      <svg width="180" height="180" viewBox="0 0 180 180" style={{ flexShrink: 0 }}>
        <circle cx={cx} cy={cy} r={r} fill="none" stroke="var(--surface-3)" strokeWidth="16" />
        {total > 0 && segments.map((s) => {
          const len = (s.value / total) * C;
          const el = (
            <circle key={s.label} cx={cx} cy={cy} r={r} fill="none" stroke={`var(--${s.color})`} strokeWidth="16"
              strokeDasharray={`${len} ${C - len}`} strokeDashoffset={-offset} strokeLinecap="butt" transform={`rotate(-90 ${cx} ${cy})`} />
          );
          offset += len;
          return el;
        })}
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize="34" fontWeight="800" fill="var(--text)" fontFamily="var(--font-sans)">{total}</text>
        <text x={cx} y={cy + 18} textAnchor="middle" fontSize="12" fill="var(--text-dim)" fontFamily="var(--font-sans)">renderers</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14, flex: 1 }}>
        {segments.map((s) => (
          <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
            <span style={{ width: 10, height: 10, borderRadius: 3, background: `var(--${s.color})`, flexShrink: 0 }} />
            <span style={{ fontSize: 13.5, color: 'var(--text-muted)', flex: 1 }}>{s.label}</span>
            <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{s.value}</span>
            <span style={{ fontSize: 12, color: 'var(--text-dim)', width: 38, textAlign: 'right' }}>{total ? Math.round((s.value / total) * 100) : 0}%</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------- Stat card (clickable) ---------- */
function DashStat({ icon, label, value, sub, tone, onClick }) {
  const [hover, setHover] = useState(false);
  const t = BADGE_TONES[tone] || BADGE_TONES.neutral;
  return (
    <button type="button" onClick={onClick} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}
      style={{ textAlign: 'left', cursor: 'pointer', fontFamily: 'var(--font-sans)',
        background: hover ? 'var(--surface-2)' : 'var(--surface-1)', border: '1px solid', borderColor: hover ? 'var(--border-strong)' : 'var(--border)',
        borderRadius: 'var(--r-lg)', padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 13,
        transition: 'background .14s, transform .1s, border-color .14s', transform: hover ? 'translateY(-2px)' : 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 11 }}>
        <span style={{ width: 36, height: 36, borderRadius: 'var(--r-md)', background: t.bg, color: t.color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}><OpusIcon name={icon} size={18} /></span>
        <span style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text-muted)' }}>{label}</span>
        <OpusIcon name="arrowRight" size={15} style={{ marginLeft: 'auto', color: 'var(--text-dim)', opacity: hover ? 1 : 0, transform: hover ? 'none' : 'translateX(-4px)', transition: 'all .14s' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
        <span style={{ fontSize: 32, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</span>
      </div>
      <span style={{ fontSize: 12.5, color: 'var(--text-dim)' }}>{sub}</span>
    </button>
  );
}

/* ---------- Recent activity row ---------- */
function ActivityRow({ a, last }) {
  const tone = ACTION_TONE[a.activity_type] || 'neutral';
  const target = a.resource_name || a.resource_type || '—';
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '13px 0', borderBottom: last ? 'none' : '1px solid var(--border)' }}>
      <Avatar name={a.user_name || '?'} size={34} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--text)' }}>{a.user_name}</span>
          <Badge tone={tone} icon={ACTION_ICON[a.activity_type]}>{a.activity_type}</Badge>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 5, minWidth: 0 }}>
          <OpusIcon name={TYPE_ICON[a.resource_type] || 'layers'} size={13} style={{ color: 'var(--text-dim)', flexShrink: 0 }} />
          <span style={{ fontSize: 12.5, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{target}</span>
        </div>
      </div>
      <span style={{ fontSize: 11.5, color: 'var(--text-dim)', whiteSpace: 'nowrap', flexShrink: 0, paddingTop: 2 }}>{moment(a.timestamp).fromNow()}</span>
    </div>
  );
}

function DashTab({ icon, label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '0 2px 12px', background: 'none', border: 'none', cursor: 'pointer',
        fontFamily: 'var(--font-sans)', fontSize: 14, fontWeight: 700, color: active ? 'var(--green)' : 'var(--text-dim)',
        borderBottom: `2px solid ${active ? 'var(--green)' : 'transparent'}`, marginBottom: -1, transition: 'color .13s', whiteSpace: 'nowrap' }}>
      <OpusIcon name={icon} size={16} />{label}
    </button>
  );
}

const DashboardHome = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const { userInfo } = useContext(AuthContext);
  const { fetchRenderersByOrg } = useContext(RendererContext);
  const { selectedOrgId: sessionOrgId, toggleDrawer, isDrawerOpen } = useSession(); // OPUS-913: follow the global sidebar org
  const [renderers, setRenderers] = useState([]);
  const [stats, setStats] = useState({ renderers: 0, activeRenderers: 0, scenes: 0, users: 0, activeUsers: 0 });
  const [userActivities, setUserActivities] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [tab, setTab] = useState('timeline');
  const [activityTimeData, setActivityTimeData] = useState([]);
  const [activityTotal, setActivityTotal] = useState(0); // OPUS-913 perf: server-provided count (no full activity fetch)

  const userRole = userInfo?.data?.role?.name;
  // OPUS-913: dashboard reflects the org selected in the sidebar (super admins
  // work across orgs); fall back to the user's own org.
  const userOrg = sessionOrgId || userInfo?.data?.organization_id?._id;
  const orgName = userInfo?.data?.organization_id?.name;
  const userName = userInfo?.data?.name || userInfo?.data?.user_name;
  const isOperator = userRole === 'operator';

  // Renderer status breakdown for the donut — uses live connectivity_status
  // (online/offline/degraded), falling back to renderer_status config.
  const connState = (r) => {
    const c = String(r.connectivity_status || "").toLowerCase();
    if (c) return c;
    return r.renderer_status === "active" ? "online" : "offline";
  };
  const donut = useMemo(() => {
    const online = renderers.filter((r) => connState(r) === 'online').length;
    const degraded = renderers.filter((r) => ['connection_degraded', 'maintenance', 'error'].includes(connState(r))).length;
    const offline = renderers.length - online - degraded;
    return [
      { label: 'Online', value: online, color: 'green' },
      { label: 'Degraded', value: degraded, color: 'amber' },
      { label: 'Offline', value: offline, color: 'blue' },
    ].filter((s) => s.value > 0 || s.label === 'Online' || s.label === 'Offline');
  }, [renderers]);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        // fetchRenderersByOrg returns { renderers: [...] } (not a bare array) —
        // unwrap defensively so a shape mismatch doesn't zero the whole dashboard.
        // OPUS-913 perf: fetch the 3 independent stat sources in parallel (was a serial waterfall).
        const [rResp, scenesResponse, usersResponse] = await Promise.all([
          fetchRenderersByOrg(userOrg),
          apiService.getScenesByOrganization(userOrg),
          apiService.getUsersByOrganization(userOrg),
        ]);
        const list = Array.isArray(rResp) ? rResp : (rResp?.renderers || rResp?.data?.renderers || []);
        const activeRenderers = list.filter((r) => connState(r) === 'online');
        const scenes = scenesResponse?.scenes || (Array.isArray(scenesResponse) ? scenesResponse : []);
        const userData = Array.isArray(usersResponse) ? usersResponse : (Array.isArray(usersResponse.data) ? usersResponse.data : []);
        const activeUsers = userData.filter((user) => user.status === 'active');
        setRenderers(list);
        setStats({
          renderers: list.length,
          activeRenderers: activeRenderers.length,
          scenes: scenes.length,
          users: userData.length,
          activeUsers: activeUsers?.length || 0,
        });
        setLoading(false);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setLoading(false);
      }
    };
    if (userOrg) fetchDashboardData();
  }, [userOrg, fetchRenderersByOrg]);

  // OPUS-913 perf: fetch only what this screen renders — server-side daily buckets
  // for the 7-day chart + a server-provided total for the count + the 10 most
  // recent rows. Previously this fetched up to 5000 full activity docs and
  // bucketed client-side (~3s / 2.4MB per load); now it's an index-served
  // aggregation (organization_id_1_timestamp_-1): a few KB in tens of ms.
  const loadActivity = React.useCallback(async () => {
    if (!userOrg) return;
    setActivityLoading(true);
    try {
      const [statsResp, recentResp] = await Promise.all([
        apiService.getActivityLogs(userOrg, { groupByDay: 7 }),
        apiService.getActivityLogs(userOrg, { limit: 10 }),
      ]);
      const daily = statsResp?.data?.daily || [];
      const byDate = {};
      for (let i = 6; i >= 0; i--) byDate[moment().subtract(i, 'days').format('YYYY-MM-DD')] = 0;
      daily.forEach((d) => { if (d.date in byDate) byDate[d.date] = d.count; });
      setActivityTimeData(Object.keys(byDate).map((k) => ({ date: moment(k).format('MMM DD'), count: byDate[k] })));
      setActivityTotal(statsResp?.data?.total || 0);
      setUserActivities(recentResp?.data?.activities || []);
    } catch (error) {
      console.error('Error fetching user activities:', error);
    } finally {
      setActivityLoading(false);
    }
  }, [userOrg]);

  useEffect(() => { loadActivity(); }, [loadActivity]);

  const handleRefreshActivities = () => { loadActivity(); };

  if (loading) {
    return <div className="opus-scroll" style={{ padding: 16, height: '100%' }}><LoadingState label="Loading dashboard…" /></div>;
  }

  // Operator role: welcome-only screen.
  if (isOperator) {
    return (
      <div style={{ padding: 16, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
        <h1 style={{ fontSize: 30, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em' }}>Welcome back, {userName}</h1>
        <p style={{ fontSize: 16, color: 'var(--coral)', marginTop: 10, fontWeight: 600 }}>{orgName}</p>
        <p style={{ fontSize: 14, color: 'var(--text-muted)', maxWidth: 560, marginTop: 18 }}>Please use the navigation menu to access available features.</p>
      </div>
    );
  }

  const totalActs = activityTimeData.reduce((a, b) => a + b.count, 0);
  const statCards = [
    { icon: 'rendererList', label: 'Renderers', value: stats.renderers, sub: `${stats.activeRenderers} online now`, tone: 'blue', onClick: () => navigate('/dashboard/renderer-management') },
    { icon: 'scene', label: 'Scenes', value: stats.scenes, sub: 'available scenes', tone: 'violet', onClick: () => navigate('/dashboard/scene-management') },
    { icon: 'users', label: 'Users', value: stats.users, sub: `${stats.activeUsers} active`, tone: 'green', onClick: () => navigate('/dashboard/user-management') },
    { icon: 'activity', label: 'Activity', value: activityTotal, sub: 'events logged', tone: 'amber', onClick: () => navigate('/dashboard/activity-logs') },
  ];

  return (
    <div className="opus-scroll" style={{ padding: 16, height: '100%', overflowY: 'auto' }}>
      {/* welcome — OPUS-1045: breadcrumb removed on the home page; a standalone
          toggle replaces the sidebar collapse control the breadcrumb carried. */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', margin: '6px 0 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <IconButton
            icon={isDrawerOpen ? 'chevronLeft' : 'chevronRight'}
            title={isDrawerOpen ? 'Collapse sidebar' : 'Expand sidebar'}
            onClick={() => toggleDrawer(!isDrawerOpen)}
          />
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontSize: 27, fontWeight: 800, color: 'var(--text)', letterSpacing: '-0.02em' }}>Welcome back, {userName}</h1>
            <p style={{ fontSize: 14, color: 'var(--text-muted)', marginTop: 4 }}>Here's what's happening across <span style={{ color: 'var(--text)', fontWeight: 600 }}>{orgName}</span> today.</p>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, flexShrink: 0 }}>
          <Button variant="secondary" icon="scene" onClick={() => navigate('/dashboard/scene-management')}>Scenes</Button>
          <Button variant="primary" icon="plus" onClick={() => navigate('/dashboard/renderer-management', { state: { openAddRenderer: true } })}>New Renderer</Button>
        </div>
      </div>

      {/* stat cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 16 }}>
        {statCards.map((s) => <DashStat key={s.label} {...s} />)}
      </div>

      {/* main grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) minmax(0,1fr)', gap: 16, alignItems: 'start' }}>
        {/* chart card */}
        <Card style={{ padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 22, borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
            <DashTab icon="activity" label="Activity Timeline" active={tab === 'timeline'} onClick={() => setTab('timeline')} />
            <DashTab icon="target" label="Renderer Status" active={tab === 'status'} onClick={() => setTab('status')} />
            <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
              <IconButton icon="refresh" size={16} title="Refresh" onClick={handleRefreshActivities} />
            </span>
          </div>
          {tab === 'timeline' ? (
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 24, fontWeight: 800, color: 'var(--text)' }}>{totalActs.toLocaleString()}</span>
                <span style={{ fontSize: 12.5, color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>activities · last 7 days</span>
              </div>
              <ActivityBars days={activityTimeData.map((d) => d.date)} values={activityTimeData.map((d) => d.count)} />
            </div>
          ) : (
            <StatusDonut segments={donut} total={renderers.length} />
          )}
        </Card>

        {/* recent activity */}
        <Card style={{ padding: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', whiteSpace: 'nowrap' }}>Recent User Activity</h2>
            <span style={{ marginLeft: 'auto', display: 'inline-flex' }}>
              <IconButton icon="refresh" size={15} title="Refresh" onClick={handleRefreshActivities} />
            </span>
          </div>
          {userActivities.length > 0 ? (
            <div>
              {userActivities.slice(0, 6).map((a, i, arr) => <ActivityRow key={a._id || i} a={a} last={i === arr.length - 1} />)}
            </div>
          ) : activityLoading ? (
            <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>Loading activity…</div>
          ) : (
            <div style={{ padding: '28px 0', textAlign: 'center', color: 'var(--text-dim)', fontSize: 13 }}>No user activities found.</div>
          )}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
            <Button variant="secondary" size="sm" icon="activity" onClick={() => navigate('/dashboard/activity-logs')}>View all activity</Button>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DashboardHome;
